import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authAPI } from '../lib/api';
import FirstRenderPage from './FirstRenderPage';

/**
 * v1.6. Tier-aware onboarding dispatcher.
 *
 *   * **Free tier** → FirstRenderPage (the "wow in 60 seconds" demo).
 *     A free user clicking through 7 setup steps before seeing a single
 *     rendered video bounced too hard to be worth fixing; the demo path
 *     puts a finished render in front of them in ~30s.
 *
 *   * **Paid tier (Solo / Team) + onboarding not yet complete** →
 *     redirect to `/setup`, which mounts the full 7-step
 *     `OnboardingWizard`. After paying, the user actually NEEDS every
 *     step (profile, Justimmo/Willhaben, templates, music, voice,
 *     socials, auto-post schedule) for their auto-posted videos to come
 *     out right. Without this guided pass they can flip on auto-post
 *     with zero socials connected, no branding, no template picks –
 *     and ship 30 broken videos before they notice.
 *
 * Stripe checkout-success also routes here on freshly-upgraded
 * accounts. The backend resets `onboarding_completed: false` on
 * subscription activation so the dispatch below sees them as paid +
 * incomplete and pushes them into the wizard.
 *
 * Old comment for context:
 *   v1.5 said the wizard was retired. v1.6 brings it back specifically
 *   for paying users. Its problems (drop-off before first render,
 *   hard walls on Justimmo/socials, friction-without-payoff on free)
 *   only applied to the free path. For paid users the calculus flips:
 *   skipping setup is the bigger product risk.
 */
export default function OnboardingPage() {
  const [decision, setDecision] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    authAPI.getMe()
      .then((r) => {
        if (cancelled) return;
        const tier = r.data?.subscription_tier || 'free';
        const completed = !!r.data?.onboarding_completed;
        // Paid + incomplete → push into the full wizard at /setup.
        // Everyone else (free of any state, paid+completed which only
        // happens via the post-wizard finalize) → render FirstRender.
        // Starter counts as "not paid" here: the 7-step wizard configures
        // auto-post/socials/imports, none of which Starter includes.
        const wantsWizard = ['pro', 'solo', 'team'].includes(tier) && !completed;
        setDecision({ wantsWizard });
      })
      .catch(() => {
        // /me failed (network blip, expired token already toasted by
        // the global interceptor). Render FirstRender as the safe
        // fallback. The OnboardingGuard upstream already verified the
        // token, so a transient failure here shouldn't trap the user.
        if (!cancelled) setDecision({ wantsWizard: false });
      });
    return () => { cancelled = true; };
  }, []);

  if (decision === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f3ea]">
        <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
      </div>
    );
  }

  if (decision.wantsWizard) {
    return <Navigate to="/setup" replace />;
  }

  return <FirstRenderPage />;
}
